import LegendToggleIcon from "@mui/icons-material/LegendToggle";
import { Button, IconButton, Tooltip as MuiTooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Box } from "@mui/system";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Area, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { CurveType } from "recharts/types/shape/Curve";
import type { AxisDomain } from "recharts/types/util/types";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { CATEGORICAL_SERIES, INK, NEUTRAL } from "../../../../theme/tokens";
import { defaultChartResponsiveHeights, defaultChartResponsiveWidths } from "../common";
import { InHouseLineChartSkeleton } from "./InHouseLineChartSkeleton";
import type { InHouseAreaDataGroup, InHouseLineChartProps, InHouseLineDataGroup } from "./types";

type MergedRow = {
	date: number; // ms since epoch
	// dynamic keys per series
	[seriesKey: string]: number | string;
};

type LineSeriesMeta = {
	key: string; // unique, used in dataset
	name: string; // for legend
	side: "left" | "right";
	color: string;
	graphCurve: CurveType;
	strokeDasharray?: string;
	strokeWidth?: number;
};

type AreaSeriesMeta = {
	key: string; // unique, used in dataset
	name: string; // for legend / tooltip
	side: "left" | "right";
	color: string;
	graphCurve: CurveType;
	areaFillOpacity: number;
	stackId: string;
};

// Functional/categorical: distinct chart series colors. Must stay stable across themes.
// Exported so callers that render their own legend/filter UI (e.g. the PBJ Audit role-filter tree)
// can cycle the SAME palette in the SAME order and keep their color dots in sync with the lines.
export const LINE_CHART_PALETTE = CATEGORICAL_SERIES;

export const InHouseLineChart: FC<InHouseLineChartProps> = ({
	width = defaultChartResponsiveWidths,
	height = defaultChartResponsiveHeights,
	leftAxisData,
	rightAxisData,
	xTickFormatter = (d) => d.toFormat("MMM d"),
	margin = {
		top: 4,
		right: 0,
		bottom: 0,
		left: 0,
	},
	xAxisType = "number",
	axisColor = NEUTRAL[600],
	lineStrokeWidth = 1.5,
	legendPadding = {
		inline: 24,
		block: 16,
	},
	legendLabelColor = alpha(INK.SECONDARY, 0.5),
	xAxisScale = "time",
	minTickGap = 8,
	hideLegend = false,
	customTooltip,
	customTooltipYOffset = 0,
	tooltipSortOrder,
	tooltipValueFormatter,
	hideZeroInTooltip = false,
	tooltipLabelFormatter: tooltipLabelFormatterProp,
	visibilityToggling = true,
	legendDescriptions,
	legendMaxHeight,
	shrinkLegendForSmallScreens = false,
	hiddenSeries: hiddenSeriesProp,
	onSeriesVisibilityToggle,
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const { t } = useTranslation();
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down(1530));
	const useShrunkLegend = shrinkLegendForSmallScreens && isSmallScreen && !hideLegend;
	const calculatedMargin = useMemo(
		() => ({
			...(useShrunkLegend ? { ...margin, bottom: margin.bottom + 12 } : margin),
		}),
		[margin, useShrunkLegend],
	);
	const [internalHiddenSeries, setInternalHiddenSeries] = useState<Set<string>>(new Set());
	// Controlled visibility when the caller passes `hiddenSeries`; internal toggle state otherwise.
	// Controlled-ness follows the prop's PRESENCE: once `hiddenSeries` is provided, legend clicks
	// only ever go to the caller's callback — never to internal state the render would ignore.
	const isControlled = hiddenSeriesProp !== undefined;
	const hiddenSeries: ReadonlySet<string> = isControlled ? hiddenSeriesProp : internalHiddenSeries;

	const toggleSeriesVisibility = useCallback(
		(seriesKey: string) => {
			if (isControlled) {
				onSeriesVisibilityToggle?.(seriesKey);
				return;
			}
			setInternalHiddenSeries((prev) => {
				const next = new Set(prev);
				if (next.has(seriesKey)) {
					next.delete(seriesKey);
				} else {
					next.add(seriesKey);
				}
				return next;
			});
		},
		[isControlled, onSeriesVisibilityToggle],
	);

	// Label formatter for tooltip (x-axis)
	const tooltipLabelFormatter = useCallback(
		// biome-ignore lint/suspicious/noExplicitAny: recharts passes the x-axis value (and label) as a loosely-typed ReactNode
		(ms: any) => {
			const d = DateTime.fromMillis(Number(ms), { zone: chosenDepartment.timezone ?? "UTC" });
			return tooltipLabelFormatterProp ? tooltipLabelFormatterProp(d) : d.toFormat("EEEE, MMMM d");
		},
		[chosenDepartment.timezone, tooltipLabelFormatterProp],
	);

	if (rightAxisData?.dataGroups && !leftAxisData?.dataGroups && !leftAxisData?.stackedAreaGroups) {
		console.warn("InHouseLineChart: rightAxisData provided without leftAxisData.");
		return "[ERROR] InHouseLineChart: rightAxisData provided without leftAxisData.";
	}

	const leftGroups = leftAxisData?.dataGroups ?? [];
	const rightGroups = rightAxisData?.dataGroups ?? [];
	const leftAreaStacks = leftAxisData?.stackedAreaGroups ?? [];
	const rightAreaStacks = rightAxisData?.stackedAreaGroups ?? [];

	// Build a consistent series key and registry
	const areaSeriesMeta: AreaSeriesMeta[] = [];
	const lineSeriesMeta: LineSeriesMeta[] = [];

	const pushSeries = (groups: InHouseLineDataGroup[], side: "left" | "right", startIndex: number) => {
		groups.forEach((g, idx) => {
			lineSeriesMeta.push({
				key: g.name,
				name: g.name,
				side,
				color: g.color || LINE_CHART_PALETTE[(startIndex + idx) % LINE_CHART_PALETTE.length],
				graphCurve: g.graphCurve || "linear",
				strokeDasharray: g.strokeDasharray,
				strokeWidth: g.strokeWidth,
			});
		});
	};

	const pushAreaStacks = (stacks: InHouseAreaDataGroup[][], side: "left" | "right", startIndex: number) => {
		stacks.forEach((stack, stackIdx) => {
			const stackId = `${side}-stack-${stackIdx}`;
			stack.forEach((g, idx) => {
				areaSeriesMeta.push({
					key: g.name,
					name: g.name,
					side,
					color: g.color || LINE_CHART_PALETTE[(startIndex + idx) % LINE_CHART_PALETTE.length],
					graphCurve: g.graphCurve || "linear",
					areaFillOpacity: g.areaFillOpacity ?? 1,
					stackId,
				});
			});
		});
	};

	pushSeries(leftGroups, "left", 0);
	pushSeries(rightGroups, "right", leftGroups.length);
	pushAreaStacks(leftAreaStacks, "left", lineSeriesMeta.length);
	pushAreaStacks(rightAreaStacks, "right", lineSeriesMeta.length + areaSeriesMeta.length);

	// Early exit: nothing to draw
	if ((!leftAxisData && !rightAxisData) || (lineSeriesMeta.length === 0 && areaSeriesMeta.length === 0)) {
		return (
			<Box
				sx={{
					width,
					height,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<InHouseLineChartSkeleton showLegendSkeleton={!hideLegend && !useShrunkLegend} margin={calculatedMargin} />
			</Box>
		);
	}

	// Empty: data provided but nothing to render
	if (lineSeriesMeta.length === 0 && areaSeriesMeta.length === 0) {
		return (
			<Box
				sx={{
					width,
					height,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Typography
					sx={{
						color: "text.secondary",
					}}
				>
					{t("No Data")}
				</Typography>
			</Box>
		);
	}

	// Merge by date (ms)
	const dateSet = new Set<number>();
	const collect = (groups: InHouseLineDataGroup[]) => {
		for (const g of groups) {
			for (const item of g.data) {
				dateSet.add(item.date.startOf("day").valueOf());
			}
		}
	};
	collect(leftGroups);
	collect(rightGroups);

	const collectAreaStacks = (stacks: InHouseAreaDataGroup[][]) => {
		for (const stack of stacks) {
			for (const g of stack) {
				for (const item of g.data) {
					dateSet.add(item.date.startOf("day").valueOf());
				}
			}
		}
	};
	collectAreaStacks(leftAreaStacks);
	collectAreaStacks(rightAreaStacks);

	const allDates = Array.from(dateSet).sort((a, b) => a - b);

	// Build rows; put undefined for missing values (Recharts handles gaps)
	const rows: MergedRow[] = allDates.map((ms) => {
		const row: MergedRow = { date: ms };
		for (const s of lineSeriesMeta) {
			row[s.key] = undefined as unknown as number;
		}
		for (const a of areaSeriesMeta) {
			row[a.key] = undefined as unknown as number;
		}
		return row;
	});

	// Helper to index date -> row
	const rowByDate = new Map<number, MergedRow>(rows.map((r) => [r.date, r]));

	const inject = (groups: InHouseLineDataGroup[]) => {
		for (const g of groups) {
			const key = g.name;
			for (const item of g.data) {
				const r = rowByDate.get(item.date.startOf("day").valueOf());
				if (r) r[key] = item.value;
			}
		}
	};
	inject(leftGroups);
	inject(rightGroups);

	const injectAreaStacks = (stacks: InHouseAreaDataGroup[][]) => {
		for (const stack of stacks) {
			for (const g of stack) {
				const key = g.name;
				for (const item of g.data) {
					const r = rowByDate.get(item.date.startOf("day").valueOf());
					if (r) r[key] = item.value;
				}
			}
		}
	};
	injectAreaStacks(leftAreaStacks);
	injectAreaStacks(rightAreaStacks);

	// Axis domains and tick formatters
	const leftDomain: AxisDomain = [leftAxisData?.minValue ?? "dataMin", leftAxisData?.maxValue ?? "dataMax"];
	const rightDomain: AxisDomain = [rightAxisData?.minValue ?? "dataMin", rightAxisData?.maxValue ?? "dataMax"];

	const yLeftTickFmt = (v: number) => (leftAxisData?.tickRenderer ? leftAxisData.tickRenderer(v) : String(v));
	const yRightTickFmt = (v: number) => (rightAxisData?.tickRenderer ? rightAxisData.tickRenderer(v) : String(v));

	// For tooltip value formatting, show series name and optionally format numeric value. Returning
	// null drops the row entirely (recharts convention) so a series with a gap at the hovered date
	// isn't listed with a blank value — keeps a many-series tooltip to the series actually present.
	const tooltipFormatter = (value: any, name: string | number | undefined, props: any) => {
		if (value === null || value === undefined) return null;
		// Densified-to-0 series list every idle day as 0; drop those so the tooltip shows only the
		// series actually active at the hovered date (matches the sparse-data tooltip behavior).
		if (hideZeroInTooltip && Number(value) === 0) return null;
		const meta = lineSeriesMeta.find((s) => s.key === name) || areaSeriesMeta.find((s) => s.key === name);
		const formatted = tooltipValueFormatter ? tooltipValueFormatter(Number(value)) : value;
		return [formatted, meta?.name ?? name];
	};

	return (
		<Box
			sx={{
				width,
				height,
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<ResponsiveContainer width={"100%"} height={"100%"}>
				<ComposedChart data={rows} margin={calculatedMargin}>
					<CartesianGrid yAxisId={"left"} strokeDasharray="2 2" />
					<XAxis
						dataKey="date"
						type={xAxisType}
						domain={["dataMin", "dataMax"]}
						tickFormatter={(ms) => xTickFormatter(DateTime.fromMillis(Number(ms), { zone: chosenDepartment.timezone ?? "UTC" }))}
						tickMargin={hideLegend ? 16 : 18}
						fontWeight={700}
						fontSize={10}
						tickSize={0}
						stroke={axisColor}
						angle={-45}
						scale={xAxisScale}
						interval={"preserveStartEnd"}
						minTickGap={minTickGap}
					/>
					{/* Left axis (main) */}
					<YAxis
						yAxisId={"left"}
						orientation={"left"}
						domain={leftDomain}
						tickFormatter={yLeftTickFmt}
						label={leftAxisData?.headerLabel ? { value: leftAxisData.headerLabel, angle: -90, position: "insideLeft" } : undefined}
						stroke={axisColor}
					/>
					{/* Optional right axis */}
					{rightGroups.length > 0 && (
						<YAxis
							yAxisId={"right"}
							orientation={"right"}
							domain={rightDomain}
							tickFormatter={yRightTickFmt}
							label={rightAxisData?.headerLabel ? { value: rightAxisData.headerLabel, angle: 90, position: "insideRight" } : undefined}
							stroke={axisColor}
						/>
					)}
					{customTooltip ? (
						<Tooltip content={(props) => customTooltip({ ...props, customTooltipYOffset })} wrapperStyle={{ zIndex: 1000 }} />
					) : (
						<Tooltip
							labelFormatter={tooltipLabelFormatter}
							formatter={tooltipFormatter}
							itemSorter={tooltipSortOrder ? (item) => tooltipSortOrder[item.name as string] ?? 999 : undefined}
							contentStyle={{ padding: "6px 10px", fontSize: 14, lineHeight: 1.3 }}
							itemStyle={{ padding: "1px 0" }}
							labelStyle={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}
						/>
					)}
					{!hideLegend && !useShrunkLegend && (
						<Legend
							align={"left"}
							verticalAlign={"bottom"}
							wrapperStyle={{
								paddingBlock: legendPadding.block,
								paddingInline: legendPadding.inline,
							}}
							content={(props) => {
								const entries = tooltipSortOrder
									? [...(props.payload ?? [])].sort((a, b) => (tooltipSortOrder[a.value as string] ?? 999) - (tooltipSortOrder[b.value as string] ?? 999))
									: props.payload;
								return (
									<div
										style={{
											display: "flex",
											gap: "16px",
											// Wrapping is opt-in via legendMaxHeight: without it legends keep their
											// long-standing single-row layout (and plot height) byte-identical.
											...(legendMaxHeight ? { flexWrap: "wrap" as const, maxHeight: legendMaxHeight, overflowY: "auto" as const } : {}),
										}}
									>
										{entries?.map((entry) => {
											const isHidden = hiddenSeries.has(entry.value as string);
											const description = legendDescriptions?.[entry.value as string];
											const button = (
												<Button
													type={"button"}
													key={`legend-item-${entry.value}`}
													tabIndex={visibilityToggling ? 0 : undefined}
													sx={{
														display: "flex",
														alignItems: "center",
														border: "none",
														gap: "4px",
														background: visibilityToggling ? undefined : "none",
														cursor: visibilityToggling ? "pointer" : "default",
														opacity: isHidden ? 0.35 : 1,
														height: "fit-content",
														width: "fit-content",
														p: 0,
													}}
													onClick={
														visibilityToggling
															? (e) => {
																	e.preventDefault();
																	e.stopPropagation();
																	toggleSeriesVisibility(entry.value as string);
																}
															: undefined
													}
													onKeyDown={
														visibilityToggling
															? (e) => {
																	if (e.key === "Enter" || e.key === " ") {
																		e.preventDefault();
																		e.stopPropagation();
																		toggleSeriesVisibility(entry.value as string);
																	}
																}
															: undefined
													}
												>
													<div
														style={{
															width: "12px",
															height: "12px",
															backgroundColor: entry.color,
															borderRadius: "50%",
														}}
													/>
													<span
														style={{
															color: legendLabelColor,
															fontSize: 12,
															fontWeight: 700,
															textDecoration: isHidden ? "line-through" : "none",
														}}
													>
														{entry.value}
													</span>
												</Button>
											);
											return description ? (
												<MuiTooltip key={`legend-tip-${entry.value}`} title={description} arrow placement="top">
													{button}
												</MuiTooltip>
											) : (
												button
											);
										})}
									</div>
								);
							}}
						/>
					)}
					{/* Stacked Area Series */}
					{areaSeriesMeta.map((a) => (
						<Area
							key={`area-${a.key}`}
							type={a.graphCurve}
							dataKey={a.key}
							yAxisId={a.side}
							stackId={a.stackId}
							stroke={a.color}
							fill={a.color}
							fillOpacity={a.areaFillOpacity}
							connectNulls
							hide={hiddenSeries.has(a.key)}
						/>
					))}
					{/* Series Lines */}
					{lineSeriesMeta.map((s) => (
						<Line
							key={`line-${s.key}`}
							type={s.graphCurve}
							dataKey={s.key}
							yAxisId={s.side}
							stroke={s.color}
							dot={false}
							activeDot={{ r: 4 }}
							connectNulls
							strokeWidth={s.strokeWidth ?? lineStrokeWidth}
							hide={hiddenSeries.has(s.key)}
							strokeDasharray={s.strokeDasharray}
						/>
					))}
				</ComposedChart>
			</ResponsiveContainer>
			{useShrunkLegend && (
				<MuiTooltip
					placement="top"
					title={
						<Box sx={{ display: "flex", flexDirection: "column", gap: "6px", p: "4px" }}>
							{(() => {
								const allMeta = [...lineSeriesMeta, ...areaSeriesMeta];
								const sorted = tooltipSortOrder ? [...allMeta].sort((a, b) => (tooltipSortOrder[a.name] ?? 999) - (tooltipSortOrder[b.name] ?? 999)) : allMeta;
								return sorted.map((s) => (
									<Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
										<Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: s.color, flexShrink: 0 }} />
										<Typography sx={{ fontSize: 12, fontWeight: 600, color: INK.ON_DARK, whiteSpace: "nowrap" }}>{s.name}</Typography>
									</Box>
								));
							})()}
						</Box>
					}
				>
					<IconButton disableRipple={true} size="small" sx={{ alignSelf: "flex-start" }} aria-label={t("Show legend")}>
						<LegendToggleIcon sx={{ fontSize: 18, color: axisColor }} />
					</IconButton>
				</MuiTooltip>
			)}
		</Box>
	);
};
